import { jsPDF } from 'jspdf';

/**
 * Premium Executeur Checklist Generator for Testamentair.com
 * Matches the existing "Strategisch Nalatenschapsdossier" aesthetic.
 * Focus: Whitespace, Elegant Typography, Minimalist Luxury.
 */
export const generateExecutorPDF = async () => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const colors = {
    navy: [15, 23, 42],      // Slate 900 / Navy
    gold: [194, 155, 85],    // Brand Gold
    slate: [71, 85, 105],    // Slate 600
    subtle: [148, 163, 184], // Slate 400
    bg: [248, 250, 252],     // Slate 50
    border: [226, 232, 240], // Slate 200
    white: [255, 255, 255]
  };

  const margin = 25;
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - (margin * 2);

  const drawFooter = (pageNum, totalPages) => {
    const footerY = 285;
    doc.setDrawColor(...colors.border);
    doc.setLineWidth(0.1);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.subtle);
    doc.text('VERTROUWELIJK EXECUTEUR DOSSIER', margin, footerY, { charSpace: 0.5 });
    doc.text(`TESTAMENTAIR.COM • PAGINA ${pageNum} VAN ${totalPages}`, pageWidth - margin, footerY, { align: 'right' });
  };

  const drawSectionHeader = (title, y) => {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.gold);
    doc.text(title.toUpperCase(), margin, y);
    doc.setDrawColor(...colors.gold);
    doc.setLineWidth(0.4);
    doc.line(margin, y + 2, margin + 10, y + 2);
    return y + 12;
  };

  const drawCheckboxRow = (text, y) => {
    doc.setDrawColor(...colors.border);
    doc.setLineWidth(0.2);
    doc.rect(margin, y - 3.5, 4, 4); // Checkbox
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.navy);
    doc.text(text, margin + 8, y);
    return y + 9;
  };

  // --- PAGE 1: COVER ---
  doc.setFillColor(...colors.bg);
  doc.rect(0, 0, 15, 297, 'F');
  
  doc.setFillColor(...colors.navy);
  doc.roundedRect(margin, 25, 8, 8, 1.5, 1.5, 'F');
  doc.setTextColor(...colors.navy);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Testamentair', margin + 12, 31);

  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.2);
  doc.roundedRect(pageWidth - margin - 35, 25, 35, 8, 1, 1, 'D');
  doc.setFontSize(7);
  doc.setTextColor(...colors.slate);
  doc.text('PRIVÉ & VERTROUWELIJK', pageWidth - margin - 17.5, 30, { align: 'center' });

  doc.setFontSize(38);
  doc.setTextColor(...colors.navy);
  doc.setFont('helvetica', 'bold');
  doc.text('Executeur', margin, 80);
  doc.text('Checklist', margin, 96);
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.slate);
  doc.text('Persoonlijk stappenplan na overlijden', margin, 108);

  // Executive Summary Box
  doc.setFillColor(...colors.bg);
  doc.roundedRect(margin, 140, contentWidth, 45, 2, 2, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.slate);
  const summary = "Deze checklist helpt executeurs en nabestaanden om overzicht te houden in de eerste periode na een overlijden. De belangrijkste praktische, financiële en administratieve stappen zijn overzichtelijk samengebracht.";
  const summaryLines = doc.splitTextToSize(summary, contentWidth - 20);
  doc.text(summaryLines, margin + 10, 155, { lineHeightFactor: 1.5 });

  // Status Indicator
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.2);
  doc.roundedRect(margin, 200, 40, 10, 1, 1, 'D');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.subtle);
  doc.text('STATUS', margin + 5, 206.5);
  doc.setTextColor(...colors.navy);
  doc.text('ACTIE VEREIST', margin + 18, 206.5);

  drawFooter(1, 3);

  // --- PAGE 2: PRAKTISCHE CHECKLIST ---
  doc.addPage();
  let yPos = 35;
  
  // Section 1: Direct Regelen
  yPos = drawSectionHeader('1. Direct Regelen', yPos);
  const direct = ['Artsverklaring ontvangen', 'Overlijdensakte aanvragen', 'Uitvaart regelen', 'Familie informeren', 'Testament controleren'];
  direct.forEach(item => { yPos = drawCheckboxRow(item, yPos); });

  yPos += 10;
  
  // Section 2: Eerste Week
  yPos = drawSectionHeader('2. Eerste Week', yPos);
  const week = ['Banken informeren', 'Verzekeringen controleren', 'Belangrijke documenten verzamelen', 'Abonnementen inventariseren', 'Pensioenfondsen informeren'];
  week.forEach(item => { yPos = drawCheckboxRow(item, yPos); });

  yPos += 10;

  // Section 3: Eerste Maand
  yPos = drawSectionHeader('3. Eerste Maand', yPos);
  const maand = ['Verklaring van erfrecht regelen', 'Boedelbeschrijving opstellen', 'Schulden inventariseren', 'Erfbelasting voorbereiden', 'Waardevolle bezittingen registreren'];
  maand.forEach(item => { yPos = drawCheckboxRow(item, yPos); });

  drawFooter(2, 3);

  // --- PAGE 3: VERGETEN ZAKEN & VERVOLGSTAPPEN ---
  doc.addPage();
  yPos = 35;

  yPos = drawSectionHeader('Wat vaak vergeten wordt', yPos);
  const vergeten = [
    'Digitale accounts controleren',
    'Automatische incasso’s stopzetten',
    'Crypto wallets controleren',
    'Kenteken / auto overschrijven',
    'Toeslagen aanpassen',
    'Buitenlandse bezittingen controleren',
    'Cloudopslag en online abonnementen bekijken'
  ];
  vergeten.forEach(item => { yPos = drawCheckboxRow(item, yPos); });

  yPos += 20;

  yPos = drawSectionHeader('Aanbevolen vervolgstappen', yPos);
  const steps = [
    'Verzamel alle financiële documentatie',
    'Houd erfgenamen actief geïnformeerd',
    'Raadpleeg een notaris bij complexe situaties',
    'Controleer fiscale aandachtspunten tijdig'
  ];
  doc.setFontSize(10);
  doc.setTextColor(...colors.navy);
  steps.forEach((step, i) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${i + 1}.`, margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(step, margin + 8, yPos);
    yPos += 10;
  });

  // Disclaimer
  const footerDividerY = 275;
  doc.setFontSize(6.5);
  doc.setTextColor(...colors.subtle);
  const disclaimer = "DISCLAIMER: Deze checklist is uitsluitend bedoeld als praktische ondersteuning voor executeurs en nabestaanden. Testamentair.com verstrekt geen juridisch bindend advies en aanvaardt geen aansprakelijkheid voor beslissingen gebaseerd op dit document.";
  const discLines = doc.splitTextToSize(disclaimer, contentWidth * 0.8);
  doc.text(discLines, margin, footerDividerY - 10, { align: 'left', lineHeightFactor: 1.3 });

  drawFooter(3, 3);
  doc.save('Executeur_Checklist_Testamentair.pdf');
};